/**
 * SRES · Portfolio-wide Signature Registry (Feb 2026 · Iter77 + Iter78).
 *
 * Superadmin tab under Administration → shows every CP/QP who has
 * signed at least one narrative paragraph, across every project, with
 * counts, standards used, roles used, and the last signed timestamp.
 *
 * A ``scope`` toggle lets the admin switch between "signed only" and
 * "all eligible" (includes users with the ``cp_signoff`` permission
 * who haven't signed anything yet).
 *
 * Iter78 filter controls (collapsed under a chevron): reporting
 * standard (multi-select), project (single-select), date range
 * (from/to). Filters travel with the CSV export.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Signature, DownloadSimple, Users, Certificate, Buildings,
  ClockCounterClockwise, ArrowsClockwise, Funnel, X,
  CaretDown, CaretUp, BookmarkSimple, FloppyDisk, Trash,
  User as UserIcon, FilePdf, Copy, EnvelopeSimple, PaperPlaneTilt,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// ═══════════════════════════════════════════════════════════════════
export default function SignatureRegistryTab() {
  const qc = useQueryClient();
  const [scope, setScope] = useState("signed");           // signed | all
  const [q, setQ]         = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStandards, setSelectedStandards] = useState([]);   // string[]
  const [selectedProject,   setSelectedProject]   = useState("");   // pid | ""
  const [dateFrom,          setDateFrom]          = useState("");   // "YYYY-MM-DD"
  const [dateTo,            setDateTo]            = useState("");

  // Iter79 · P1 · Saved filter presets ────────────────────────────
  const [selectedPresetId,  setSelectedPresetId]  = useState("");
  const [savePresetOpen,    setSavePresetOpen]    = useState(false);
  const [savePresetName,    setSavePresetName]    = useState("");
  const [deletePresetTarget, setDeletePresetTarget] = useState(null);

  const presetsQ = useQuery({
    queryKey: ["signature-registry-presets"],
    queryFn:  () => api.get("/admin/signature-registry/presets").then((r) => r.data),
    staleTime: 15_000,
  });

  const savePresetMut = useMutation({
    mutationFn: (body) =>
      api.post("/admin/signature-registry/presets", body).then((r) => r.data),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ["signature-registry-presets"] });
      setSelectedPresetId(doc.id);
      setSavePresetOpen(false);
      setSavePresetName("");
      toast.success(`Preset '${doc.name}' saved.`);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message
        || e?.response?.data?.detail
        || "Save failed.");
    },
  });

  const deletePresetMut = useMutation({
    mutationFn: (id) =>
      api.delete(`/admin/signature-registry/presets/${id}`).then((r) => r.data),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["signature-registry-presets"] });
      if (selectedPresetId === id) setSelectedPresetId("");
      setDeletePresetTarget(null);
      toast.success("Preset deleted.");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message || "Delete failed.");
    },
  });

  // Iter80 · P1 · Duplicate preset — auto-saves with " (copy N)" suffix.
  const duplicatePresetMut = useMutation({
    mutationFn: (id) =>
      api.post(`/admin/signature-registry/presets/${id}/duplicate`)
              .then((r) => r.data),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ["signature-registry-presets"] });
      setSelectedPresetId(doc.id);
      toast.success(`Preset duplicated as '${doc.name}'.`);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message
                     || e?.response?.data?.detail
                     || "Duplicate failed.");
    },
  });

  // Iter80 · P2 · Batch-email PDFs ────────────────────────────────
  const [batchOpen,       setBatchOpen]       = useState(false);
  const [batchPresetIds,  setBatchPresetIds]  = useState([]);
  const [batchRecipients, setBatchRecipients] = useState("");
  const [batchSubject,    setBatchSubject]    = useState("");
  const [batchMessage,    setBatchMessage]    = useState("");
  const [lastDispatch,    setLastDispatch]    = useState(null);

  const historyQ = useQuery({
    queryKey: ["signature-registry-batch-history"],
    queryFn:  () => api.get("/admin/signature-registry/batch-email/history?limit=5")
                          .then((r) => r.data),
    staleTime: 30_000,
    enabled:  batchOpen,             // only refresh when dialog opens
  });

  const batchEmailMut = useMutation({
    mutationFn: (body) =>
      api.post("/admin/signature-registry/batch-email", body).then((r) => r.data),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ["signature-registry-batch-history"] });
      setLastDispatch(doc);
      const delivered = doc?.delivery?.delivered;
      if (delivered) {
        toast.success(
          `Sent ${doc.count} PDF${doc.count === 1 ? "" : "s"} to ${doc.recipients.length} recipient${doc.recipients.length === 1 ? "" : "s"}.`
        );
      } else {
        toast.warning(
          `Dispatch recorded (id ${doc.id}), but delivery failed: ${
            doc?.delivery?.reason?.slice?.(0, 120) || "unknown"
          }`
        );
      }
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message
                     || e?.response?.data?.detail
                     || "Batch email failed.");
    },
  });

  const toggleBatchPresetId = (id) => {
    setBatchPresetIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submitBatchEmail = () => {
    if (batchPresetIds.length === 0) {
      toast.error("Select at least one preset to include.");
      return;
    }
    if (!batchRecipients.trim()) {
      toast.error("Enter at least one recipient email.");
      return;
    }
    batchEmailMut.mutate({
      recipients:  [batchRecipients],
      preset_ids:  batchPresetIds,
      subject:     batchSubject.trim() || undefined,
      message:     batchMessage.trim() || undefined,
    });
  };

  const applyPreset = (preset) => {
    if (!preset) return;
    setScope(preset.scope || "signed");
    setSelectedStandards(preset.standards || []);
    setSelectedProject(preset.project_id || "");
    setDateFrom(preset.date_from || "");
    setDateTo(preset.date_to || "");
    setSelectedPresetId(preset.id);
    toast.success(`Loaded preset '${preset.name}'.`);
  };

  const onPresetSelectChange = (id) => {
    setSelectedPresetId(id);
    if (!id) return;
    const p = (presetsQ.data?.items || []).find((x) => x.id === id);
    if (p) applyPreset(p);
  };

  const savePreset = () => {
    const name = (savePresetName || "").trim();
    if (!name) {
      toast.error("Please enter a preset name.");
      return;
    }
    savePresetMut.mutate({
      name,
      scope,
      standards:  selectedStandards,
      project_id: selectedProject || null,
      date_from:  dateFrom || null,
      date_to:    dateTo   || null,
    });
  };

  // Facets — used to populate the filter selectors.
  const facetsQ = useQuery({
    queryKey: ["signature-registry-facets"],
    queryFn:  () => api.get("/admin/signature-registry/facets").then((r) => r.data),
    staleTime: 60_000,
  });

  const activeFilterCount = (selectedStandards.length ? 1 : 0)
                              + (selectedProject ? 1 : 0)
                              + (dateFrom || dateTo ? 1 : 0);

  // Build query params identically for JSON and CSV.
  const filterParams = useMemo(() => {
    const parts = [`scope=${scope}`];
    for (const s of selectedStandards) parts.push(`standard=${encodeURIComponent(s)}`);
    if (selectedProject) parts.push(`project_id=${encodeURIComponent(selectedProject)}`);
    if (dateFrom) parts.push(`from=${encodeURIComponent(dateFrom)}`);
    if (dateTo)   parts.push(`to=${encodeURIComponent(dateTo)}`);
    return parts.join("&");
  }, [scope, selectedStandards, selectedProject, dateFrom, dateTo]);

  const registryQ = useQuery({
    queryKey: ["signature-registry", filterParams],
    queryFn:  () => api.get(`/admin/signature-registry?${filterParams}`).then((r) => r.data),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const rows = useMemo(() => registryQ.data?.items || [], [registryQ.data]);
  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const qq = q.toLowerCase();
    return rows.filter((r) =>
      (r.actor_email || "").toLowerCase().includes(qq)
        || (r.actor_name  || "").toLowerCase().includes(qq)
        || (r.actor_role  || "").toLowerCase().includes(qq)
        || (r.roles || []).some((x) => x.toLowerCase().includes(qq))
        || (r.standards || []).some((x) => x.toLowerCase().includes(qq))
        || (r.projects || []).some((p) => (p.project_name || "").toLowerCase().includes(qq))
    );
  }, [rows, q]);

  const totalSignatures = registryQ.data?.total_signatures || 0;
  const eligibleUsers   = registryQ.data?.eligible_users;

  const downloadCsv = async () => {
    try {
      const { data } = await api.get(
        `/admin/signature-registry.csv?${filterParams}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `signature-registry-${scope}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Registry CSV downloaded.");
    } catch (e) {
      toast.error("CSV export failed.");
    }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(
        `/admin/signature-registry.pdf?${filterParams}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, "");
      a.download = `signature-registry-${scope}-${stamp}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit PDF downloaded (SHA-256 fingerprint on cover).");
    } catch (e) {
      toast.error("PDF export failed.");
    }
  };

  const clearFilters = () => {
    setSelectedStandards([]);
    setSelectedProject("");
    setDateFrom("");
    setDateTo("");
  };

  const toggleStandard = (s) => {
    setSelectedStandards((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  return (
    <div data-testid="signature-registry-tab" className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Total signatures"
          value={totalSignatures}
          icon={Signature} tone="emerald"
          testid="sr-kpi-signatures"
        />
        <KpiTile
          label="Signers on record"
          value={rows.filter((r) => r.signature_count > 0).length}
          icon={Users} tone="brand-blue"
          testid="sr-kpi-signers"
        />
        <KpiTile
          label="Reporting standards"
          value={new Set(rows.flatMap((r) => r.standards || [])).size}
          icon={Certificate} tone="amber-500"
          testid="sr-kpi-standards"
        />
        <KpiTile
          label={scope === "signed" ? "Projects touched" : "Eligible users"}
          value={scope === "signed"
            ? new Set(rows.flatMap((r) => (r.projects || []).map((p) => p.project_id))).size
            : eligibleUsers ?? 0}
          icon={Buildings} tone="rose-500"
          testid="sr-kpi-projects"
        />
      </div>

      {/* Filters + actions */}
      <div className="flex items-center gap-3 flex-wrap"
              data-testid="sr-toolbar">
        <div className="inline-flex rounded border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setScope("signed")}
            data-testid="sr-scope-signed"
            className={`px-3 py-1.5 text-[11.5px] font-semibold ${
              scope === "signed"
                ? "bg-brand-blue text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Signed only
          </button>
          <button
            type="button"
            onClick={() => setScope("all")}
            data-testid="sr-scope-all"
            className={`px-3 py-1.5 text-[11.5px] font-semibold ${
              scope === "all"
                ? "bg-brand-blue text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            All CP/QP eligible
          </button>
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, name, role, standard, project…"
          className="h-9 text-[12.5px] max-w-md"
          data-testid="sr-search"
        />
        <Button
          variant={activeFilterCount > 0 ? "default" : "outline"}
          size="sm"
          onClick={() => setShowFilters((v) => !v)}
          data-testid="sr-filters-toggle"
          className={`h-9 text-[11.5px] gap-1 ${
            activeFilterCount > 0
              ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
              : ""
          }`}
        >
          <Funnel size={12} weight={activeFilterCount > 0 ? "fill" : "regular"} />
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          {showFilters ? <CaretUp size={10} /> : <CaretDown size={10} />}
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm"
                    onClick={() => registryQ.refetch()}
                    data-testid="sr-refresh"
                    className="h-9 text-[11.5px] gap-1">
          <ArrowsClockwise size={12} />
          {registryQ.isFetching ? "Refreshing…" : "Refresh"}
        </Button>
        <Button size="sm" onClick={downloadCsv}
                    data-testid="sr-export-csv"
                    className="h-9 text-[11.5px] gap-1
                                    bg-brand-blue hover:bg-brand-blue/90 text-white">
          <DownloadSimple size={12} weight="bold" />
          Export CSV
        </Button>
        <Button size="sm" onClick={downloadPdf}
                    data-testid="sr-export-pdf"
                    title="Audit-ready PDF with SHA-256 fingerprint on the cover"
                    className="h-9 text-[11.5px] gap-1
                                    bg-slate-900 hover:bg-slate-800 text-white">
          <FilePdf size={12} weight="bold" />
          Export PDF
        </Button>
      </div>

      {/* Iter78 · Filter panel (collapsible) */}
      {showFilters && (
        <div
          data-testid="sr-filter-panel"
          className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4"
        >
          {/* Iter79 · Saved presets bar */}
          <div
            data-testid="sr-presets-bar"
            className="rounded border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 flex-wrap"
          >
            <BookmarkSimple size={13} className="text-brand-blue shrink-0" />
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold whitespace-nowrap">
              Saved presets
            </label>
            <select
              value={selectedPresetId}
              onChange={(e) => onPresetSelectChange(e.target.value)}
              data-testid="sr-preset-select"
              className="h-8 rounded border border-slate-200 bg-white px-2 text-[12px] min-w-[220px] flex-1 sm:flex-none"
            >
              <option value="">— Choose a preset —</option>
              {(presetsQ.data?.items || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {selectedPresetId && (() => {
              const p = (presetsQ.data?.items || []).find(
                (x) => x.id === selectedPresetId);
              if (!p) return null;
              return (
                <span
                  data-testid="sr-preset-meta"
                  className="text-[10.5px] text-slate-500 inline-flex items-center gap-1"
                  title={`Created by ${p.owner_email}\nLast updated ${new Date(p.updated_at).toLocaleString()} by ${p.updater_email || p.owner_email}`}
                >
                  <UserIcon size={10} />
                  <span className="truncate max-w-[180px]">{p.owner_name || p.owner_email}</span>
                  <span className="text-slate-400">·</span>
                  <ClockCounterClockwise size={10} />
                  {new Date(p.updated_at).toLocaleDateString()}
                </span>
              );
            })()}
            <div className="flex-1" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if ((presetsQ.data?.items || []).length === 0) {
                  toast.error("Create a preset first to email its PDF.");
                  return;
                }
                setBatchPresetIds(selectedPresetId ? [selectedPresetId] : []);
                setBatchRecipients("");
                setBatchSubject("");
                setBatchMessage("");
                setLastDispatch(null);
                setBatchOpen(true);
              }}
              data-testid="sr-preset-batch-email"
              title="Email PDFs of selected presets as a zip attachment"
              className="h-8 text-[11px] gap-1"
            >
              <EnvelopeSimple size={11} weight="bold" />
              Email PDFs
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSavePresetName("");
                setSavePresetOpen(true);
              }}
              data-testid="sr-preset-save-open"
              className="h-8 text-[11px] gap-1"
            >
              <FloppyDisk size={11} weight="bold" />
              Save current…
            </Button>
            {selectedPresetId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => duplicatePresetMut.mutate(selectedPresetId)}
                disabled={duplicatePresetMut.isPending}
                data-testid="sr-preset-duplicate"
                title="Duplicate this preset with a ' (copy)' suffix"
                className="h-8 text-[11px] gap-1"
              >
                <Copy size={11} weight="bold" />
                {duplicatePresetMut.isPending ? "Duplicating…" : "Duplicate"}
              </Button>
            )}
            {selectedPresetId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const p = (presetsQ.data?.items || []).find(
                    (x) => x.id === selectedPresetId);
                  if (p) setDeletePresetTarget(p);
                }}
                data-testid="sr-preset-delete-open"
                className="h-8 text-[11px] gap-1 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              >
                <Trash size={11} weight="bold" />
                Delete
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Reporting standards */}
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">
              Reporting Standard
            </label>
            <div className="flex flex-wrap gap-1"
                    data-testid="sr-filter-standards">
              {(facetsQ.data?.standards || []).map((s, idx) => {
                const active = selectedStandards.includes(s);
                return (
                  <button
                    key={`${idx}-${s}`}
                    type="button"
                    onClick={() => toggleStandard(s)}
                    data-testid={`sr-standard-${idx}-${s.replace(/[^A-Za-z0-9]/g, "_")}`}
                    data-standard-value={s}
                    className={`px-2 py-1 rounded border text-[10.5px] font-semibold ${
                      active
                        ? "bg-brand-blue text-white border-brand-blue"
                        : "bg-white text-slate-700 border-slate-200 hover:border-brand-blue/40"
                    }`}
                  >
                    {s.replace(/_/g, " ")}
                  </button>
                );
              })}
              {facetsQ.isLoading && (
                <span className="text-[10.5px] text-slate-400">Loading…</span>
              )}
              {!facetsQ.isLoading && (facetsQ.data?.standards || []).length === 0 && (
                <span className="text-[10.5px] text-slate-400">No standards recorded.</span>
              )}
            </div>
          </div>

          {/* Project */}
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              data-testid="sr-filter-project"
              className="w-full h-9 rounded border border-slate-200 bg-white px-2 text-[12.5px]"
            >
              <option value="">All projects</option>
              {(facetsQ.data?.projects || []).map((p) => {
                // Build the label as a single string so React never
                // sees mixed children inside <option> (some browsers +
                // React dev-mode warn about non-text nodes inside).
                const label = (p.name || (p.id || "").slice(0, 12))
                  + " · "
                  + (p.reporting_standard || "").replace(/_/g, " ");
                return (
                  <option key={p.id} value={p.id}>{label}</option>
                );
              })}
            </select>
          </div>

          {/* Date range */}
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">
              Date range (signed)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="sr-filter-date-from"
                className="flex-1 h-9 rounded border border-slate-200 bg-white px-2 text-[12.5px]"
              />
              <span className="text-slate-400 text-[11.5px]">–</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="sr-filter-date-to"
                className="flex-1 h-9 rounded border border-slate-200 bg-white px-2 text-[12.5px]"
              />
            </div>
            {facetsQ.data?.date_bounds?.earliest && (
              <div className="mt-1 text-[10px] text-slate-400">
                Available: {new Date(facetsQ.data.date_bounds.earliest).toLocaleDateString()}
                {" → "}
                {new Date(facetsQ.data.date_bounds.latest).toLocaleDateString()}
              </div>
            )}
          </div>
          {/* end grid */}
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
              <span className="text-[10.5px] text-slate-500">
                {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active
              </span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={clearFilters}
                data-testid="sr-filter-clear"
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded text-slate-600 hover:bg-slate-100"
              >
                <X size={11} /> Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Registry table */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden"
              data-testid="sr-registry-table">
        {registryQ.isLoading && (
          <div className="p-6 text-center text-slate-500 text-sm">
            Loading registry…
          </div>
        )}
        {registryQ.isSuccess && filtered.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-sm border-dashed border border-slate-200 m-4 rounded"
                    data-testid="sr-empty">
            No records match the current filter.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="table-wrapper">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-100 text-slate-600 text-[10.5px] uppercase tracking-widest">
                <tr>
                  <th className="text-left  px-3 py-2">Signer</th>
                  <th className="text-left  px-3 py-2" data-mobile-hide="md">Role · Perms</th>
                  <th className="text-right px-3 py-2">Signatures</th>
                  <th className="text-right px-3 py-2" data-mobile-hide="sm">Projects</th>
                  <th className="text-right px-3 py-2" data-mobile-hide="xs">Chapters</th>
                  <th className="text-left  px-3 py-2" data-mobile-hide="md">Standards</th>
                  <th className="text-left  px-3 py-2" data-mobile-hide="xs">Roles used</th>
                  <th className="text-left  px-3 py-2" data-mobile-hide="sm">Last signed</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <RegistryRow key={(r.actor_email || i) + ":" + scope} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[10.5px] text-slate-400" data-testid="sr-footnote">
        Registry aggregates <code className="font-mono">narrative_signatures</code>
        {" "}documents across every project. Signatures are SHA-256 hashed and
        time-stamped immutably.
      </p>

      {/* Iter79 · Save-preset dialog */}
      <Dialog open={savePresetOpen} onOpenChange={(o) => !o && setSavePresetOpen(false)}>
        <DialogContent data-testid="sr-save-preset-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save current filter as preset</DialogTitle>
            <DialogDescription>
              Give this filter combination a name. Presets are shared with every
              super-administrator on the portfolio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                Preset name
              </label>
              <Input
                autoFocus
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                placeholder="e.g. Q1-2026 · JORC only"
                data-testid="sr-save-preset-name"
                className="text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); savePreset(); }
                }}
              />
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[11px] space-y-1"
                    data-testid="sr-save-preset-summary">
              <div><b>Scope:</b> {scope}</div>
              <div><b>Standards:</b> {selectedStandards.length ? selectedStandards.join(", ") : "(any)"}</div>
              <div><b>Project:</b> {selectedProject || "(all)"}</div>
              <div><b>Date range:</b> {dateFrom || "—"} → {dateTo || "—"}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetOpen(false)}
                        data-testid="sr-save-preset-cancel">
              Cancel
            </Button>
            <Button onClick={savePreset}
                        disabled={savePresetMut.isPending || !savePresetName.trim()}
                        data-testid="sr-save-preset-submit"
                        className="bg-brand-blue hover:bg-brand-blue/90 text-white">
              {savePresetMut.isPending ? "Saving…" : "Save preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter79 · Delete-preset confirmation */}
      <Dialog open={!!deletePresetTarget}
                  onOpenChange={(o) => !o && setDeletePresetTarget(null)}>
        <DialogContent data-testid="sr-delete-preset-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete preset?</DialogTitle>
            <DialogDescription>
              This will remove the saved preset for every super-administrator.
              Existing filter selections are unaffected.
            </DialogDescription>
          </DialogHeader>
          {deletePresetTarget && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-[12px] my-2"
                    data-testid="sr-delete-preset-summary">
              <div className="font-semibold text-rose-800">
                {deletePresetTarget.name}
              </div>
              <div className="text-[10.5px] text-rose-700 mt-0.5">
                Created by {deletePresetTarget.owner_email} on{" "}
                {new Date(deletePresetTarget.created_at).toLocaleDateString()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePresetTarget(null)}
                        data-testid="sr-delete-preset-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => deletePresetTarget && deletePresetMut.mutate(deletePresetTarget.id)}
              disabled={deletePresetMut.isPending}
              data-testid="sr-delete-preset-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deletePresetMut.isPending ? "Deleting…" : "Delete preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter80 · Batch-email PDFs dialog */}
      <Dialog open={batchOpen}
                  onOpenChange={(o) => !o && setBatchOpen(false)}>
        <DialogContent data-testid="sr-batch-email-dialog"
                            className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeSimple size={16} className="text-brand-blue" />
              Email registry PDFs
            </DialogTitle>
            <DialogDescription>
              Generate one audit PDF per selected preset (each with a
              SHA-256 fingerprint on its cover) and send them as a
              zipped attachment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Preset multi-select */}
            <div>
              <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-1.5">
                Presets to include ({batchPresetIds.length})
              </label>
              <div
                data-testid="sr-batch-preset-list"
                className="rounded border border-slate-200 max-h-48 overflow-y-auto"
              >
                {(presetsQ.data?.items || []).length === 0 && (
                  <div className="p-3 text-[11.5px] text-slate-500 text-center">
                    No presets saved yet.
                  </div>
                )}
                {(presetsQ.data?.items || []).map((p) => {
                  const active = batchPresetIds.includes(p.id);
                  return (
                    <label key={p.id}
                                data-testid={`sr-batch-preset-${p.id}`}
                                className={`flex items-start gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer text-[12.5px] ${
                                  active ? "bg-brand-blue/5" : "hover:bg-slate-50"
                                }`}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleBatchPresetId(p.id)}
                        data-testid={`sr-batch-preset-checkbox-${p.id}`}
                        className="mt-1 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">
                          {p.scope} · {(p.standards?.length ? p.standards.join(", ") : "any standard")}
                          {p.project_id ? ` · project ${p.project_id.slice(0, 8)}…` : ""}
                          {p.date_from || p.date_to ? ` · ${p.date_from || "…"} → ${p.date_to || "…"}` : ""}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Recipients */}
            <div>
              <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                Recipient emails (comma or newline separated)
              </label>
              <Textarea
                value={batchRecipients}
                onChange={(e) => setBatchRecipients(e.target.value)}
                rows={2}
                placeholder="ceo@company.com, auditor@firm.com"
                data-testid="sr-batch-recipients"
                className="text-[12.5px] font-mono"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Max 25 unique recipients per dispatch.
              </div>
            </div>

            {/* Subject + message */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                  Subject (optional)
                </label>
                <Input
                  value={batchSubject}
                  onChange={(e) => setBatchSubject(e.target.value)}
                  placeholder="Overrides the default subject line"
                  data-testid="sr-batch-subject"
                />
              </div>
              <div>
                <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">
                  Message (optional)
                </label>
                <Textarea
                  value={batchMessage}
                  onChange={(e) => setBatchMessage(e.target.value)}
                  rows={3}
                  placeholder="Add a short intro that appears above the manifest."
                  data-testid="sr-batch-message"
                  className="text-[12.5px]"
                />
              </div>
            </div>

            {/* Last dispatch confirmation */}
            {lastDispatch && (
              <div
                data-testid="sr-batch-last-dispatch"
                className={`rounded border p-3 text-[11.5px] ${
                  lastDispatch?.delivery?.delivered
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                }`}
              >
                <div className="font-semibold">
                  {lastDispatch?.delivery?.delivered
                    ? `Delivered — ${lastDispatch.count} PDF${lastDispatch.count === 1 ? "" : "s"} to ${lastDispatch.recipients.length} recipient${lastDispatch.recipients.length === 1 ? "" : "s"}.`
                    : "Dispatch recorded but delivery failed."}
                </div>
                <div className="mt-1 font-mono text-[10.5px] break-all">
                  ZIP SHA-256: {lastDispatch.zip_fingerprint?.slice(0, 32)}…
                </div>
                {lastDispatch?.delivery?.reason && (
                  <div className="mt-1 text-[10.5px]">Reason: {lastDispatch.delivery.reason}</div>
                )}
              </div>
            )}

            {/* Recent history */}
            {(historyQ.data?.items || []).length > 0 && (
              <details className="rounded border border-slate-200 bg-slate-50 p-2"
                            data-testid="sr-batch-history">
                <summary className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold cursor-pointer">
                  Recent dispatches ({historyQ.data.count})
                </summary>
                <div className="mt-2 space-y-1">
                  {(historyQ.data?.items || []).map((h) => (
                    <div key={h.id}
                                className="flex items-center gap-2 text-[10.5px]">
                      <ClockCounterClockwise size={10} className="text-slate-400" />
                      <span className="text-slate-500 truncate flex-1">
                        {new Date(h.created_at).toLocaleString()} · {h.selection_count} PDF{h.selection_count === 1 ? "" : "s"} → {h.recipients?.length || 0} recipient(s)
                      </span>
                      <span className={h?.delivery?.delivered
                        ? "text-emerald-600"
                        : "text-amber-600"}>
                        {h?.delivery?.delivered ? "✓" : "!"}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline"
                        onClick={() => setBatchOpen(false)}
                        data-testid="sr-batch-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitBatchEmail}
              disabled={batchEmailMut.isPending
                          || batchPresetIds.length === 0
                          || !batchRecipients.trim()}
              data-testid="sr-batch-send"
              className="gap-1 bg-brand-blue hover:bg-brand-blue/90 text-white"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {batchEmailMut.isPending
                ? "Sending…"
                : `Send ${batchPresetIds.length || ""} PDF${batchPresetIds.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone, testid }) {
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3"
      data-testid={testid}
    >
      <div className={`h-10 w-10 rounded-lg bg-${tone}/10 text-${tone} flex items-center justify-center shrink-0`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </div>
        <div className="text-2xl font-black text-slate-900 leading-tight truncate">
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
      </div>
    </div>
  );
}

function RegistryRow({ row }) {
  const [expand, setExpand] = useState(false);
  const perms = row.permissions || [];
  const emailKey = (row.actor_email || "unknown").replace(/[^a-zA-Z0-9]/g, "_");
  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50 align-top"
                data-testid={`sr-row-${emailKey}`}>
        <td className="px-3 py-2">
          <div className="font-semibold text-slate-900">
            {row.actor_name || <span className="italic text-slate-400">Unnamed</span>}
          </div>
          <div className="text-[10.5px] font-mono text-slate-500 truncate">
            {row.actor_email || "—"}
          </div>
        </td>
        <td className="px-3 py-2" data-mobile-hide="md">
          <span className="inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
            {row.actor_role || "user"}
          </span>
          {perms.length > 0 && (
            <div className="text-[10px] text-slate-400 mt-1">
              {perms.slice(0, 3).join(" · ")}
              {perms.length > 3 && ` +${perms.length - 3}`}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right font-mono font-semibold">
          {row.signature_count || 0}
        </td>
        <td className="px-3 py-2 text-right font-mono" data-mobile-hide="sm">
          {(row.projects || []).length > 0 ? (
            <button
              type="button"
              onClick={() => setExpand((v) => !v)}
              data-testid={`sr-expand-${emailKey}`}
              className="text-brand-blue hover:underline"
            >
              {row.project_count || 0} {expand ? "▴" : "▾"}
            </button>
          ) : row.project_count || 0}
        </td>
        <td className="px-3 py-2 text-right font-mono" data-mobile-hide="xs">{row.chapter_count || 0}</td>
        <td className="px-3 py-2" data-mobile-hide="md">
          {(row.standards || []).map((s) => (
            <span key={s}
                        className="mr-1 mb-1 inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-brand-blue/30 bg-brand-blue/5 text-brand-blue">
              {s.replace(/_/g, " ")}
            </span>
          ))}
        </td>
        <td className="px-3 py-2" data-mobile-hide="xs">
          {(row.roles || []).map((r) => (
            <span key={r}
                        className="mr-1 mb-1 inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700">
              {r.toUpperCase()}
            </span>
          ))}
        </td>
        <td className="px-3 py-2 text-[10.5px] text-slate-500 whitespace-nowrap" data-mobile-hide="sm">
          <span className="inline-flex items-center gap-1">
            <ClockCounterClockwise size={11} />
            {row.last_signed_at
              ? new Date(row.last_signed_at).toLocaleString()
              : "—"}
          </span>
        </td>
      </tr>
      {expand && (row.projects || []).length > 0 && (
        <tr className="border-t border-slate-100 bg-slate-50/50">
          <td colSpan={8} className="px-6 py-3"
                data-testid={`sr-projects-${emailKey}`}>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-2">
              Projects signed
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {(row.projects || []).map((p) => (
                <div key={p.project_id}
                     className="rounded border border-slate-200 bg-white px-3 py-2 text-[11.5px]">
                  <div className="font-semibold text-slate-900 truncate">
                    {p.project_name || <span className="italic text-slate-400">Untitled</span>}
                  </div>
                  <div className="text-[10.5px] font-mono text-slate-500 truncate">
                    {p.project_id?.slice(0, 12)}…
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10.5px] text-slate-500">
                    <span className="inline-block text-[9px] uppercase tracking-widest px-1 py-0.5 rounded border border-brand-blue/30 bg-brand-blue/5 text-brand-blue">
                      {(p.reporting_standard || "").replace(/_/g, " ")}
                    </span>
                    <span className="font-mono">{p.count} signatures</span>
                    {p.last_signed_at && (
                      <span className="text-slate-400 truncate">
                        · {new Date(p.last_signed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
